import { ReactRenderer } from '@tiptap/react'
import tippy, { type GetReferenceClientRect, type Instance } from 'tippy.js'

import type { SuggestionOptions } from '@tiptap/suggestion'
import { PlaceholderList } from './PlaceholderList'
import { getPlaceholderData, type GroupedPlaceholderItems } from './placeholderData'

const placeholderSuggestion = (placeholderCategories?: string[]): Omit<SuggestionOptions<GroupedPlaceholderItems>, 'editor'> => ({
    char: '/',
    command: ({ editor, range, props }) => {
        editor
            .chain()
            .focus()
            .deleteRange(range)
            .insertContent(
                `<span data-placeholder-node data-category="${props.category}" data-placeholder="${props.placeholder
                }" data-label="${props.label}">${props.placeholder}</span> `
            )
            .run()
    },

    items: ({ query }: { query: string }) => {
        if (!query) {
            return [getPlaceholderData({ categoriesFilter: placeholderCategories })]
        }

        // Filter categories and items based on query
        const filtered = getPlaceholderData({ categoriesFilter: placeholderCategories })
            .map(category => {
                // Check if category matches the query
                const categoryMatch = category.category.toLowerCase().includes(query.toLowerCase())

                // Filter items within the category
                const filteredItems = category.items.filter(item =>
                    item.label.toLowerCase().includes(query.toLowerCase())
                )

                // Return category with filtered items if there are matches
                if (categoryMatch || filteredItems.length > 0) {
                    return {
                        ...category,
                        items: filteredItems.length > 0 ? filteredItems : categoryMatch ? category.items : []
                    }
                }

                return null
            })
            .filter(Boolean) as GroupedPlaceholderItems

        return [filtered]
    },

    allowSpaces: false,

    render: () => {
        let component: ReactRenderer<any, any>
        let popup: Instance[]

        return {
            onStart: (props) => {
                component = new ReactRenderer(PlaceholderList, {
                    props,
                    editor: props.editor
                })

                popup = tippy('body', {
                    getReferenceClientRect: props.clientRect as GetReferenceClientRect,
                    content: component.element,
                    showOnCreate: true,
                    interactive: true,
                    trigger: 'manual',
                    placement: 'bottom-start',
                    theme: 'light'
                })
            },

            onUpdate(props) {
                component.updateProps(props)

                if (!props.clientRect) {
                    return
                }

                popup[0]?.setProps({
                    getReferenceClientRect: props.clientRect as GetReferenceClientRect
                })
            },

            onKeyDown({ event }: { event: KeyboardEvent }) {
                if (event.key === 'Escape') {
                    popup[0]?.hide()
                    component.destroy()

                    return true
                }

                return component.ref?.onKeyDown({ event })
            },

            onExit() {
                popup[0]?.destroy()
                component.destroy()
            }
        }
    }
})

export default placeholderSuggestion
